import '@babel/polyfill/noConflict';

import { isIE } from 'react-device-detect';

import authRoutes from 'containers/Authentication/routes';
import AcctXfer from 'containers/Landing/AcctXfer/Loadable';
import BaseLayout from 'containers/Layout/BaseLayout';
import CANoticeAtCollection from 'containers/Landing/CANoticeAtCollection/Loadable';
import CaliforniaTransparencyAct from 'containers/Landing/CaliforniaTransparencyAct/Loadable';
import PageNotFound from 'containers/Landing/PageNotFound/Loadable';
import PayMyBill from 'containers/PrimoProfile/PayMyBill/Loadable';
import PrimoAccount from 'containers/PrimoAccount/Loadable';
import PrimoFAQ from 'containers/Landing/FAQ/primo/Loadable';
import PrimoProfile from 'containers/PrimoProfile/Loadable';
import PrivacyPolicy from 'containers/Landing/PrivacyPolicy/Loadable';
import Refer from 'containers/Landing/Refer/Loadable';
import SomethingWentWrong from 'containers/SomethingWentWrong/Loadable';
import Support from 'containers/Landing/Support/Loadable';
import TermsAndConditions from 'containers/Landing/TermsAndConditions/Loadable';
import TermsOfUse from 'containers/Landing/TermsOfUse/Loadable';
import TexasNoticeAtCollection from 'containers/Landing/TexasNoticeAtCollection/Loadable';
import UnsupportedBrowser from 'containers/UnsupportedBrowser/Loadable';
import WaterLogin from 'containers/PrimoAccount/Login/Loadable';
import { ProductList } from 'containers/PrimoProducts/ProductListPage';
import { ProductPageLoggedIn } from 'containers/PrimoProducts/ProductPage';
import { pages, REGIONAL_DOMAIN } from 'containers/Landing/constants';

/**
 * To declare route use object with next params:
 * - path: (required) Route path
 * - component: (required) Route component
 * - key: (required) Key name for route entry
 * - layout: Layout component that used as wrapper for child
 * - privateRoute: restrict access to route for only authenticated users
 * - shared: if true then this page is shared between 'office' and 'home' sections, also this parameter adds ?section=office query param
 * - anonymousOnly: restrict access to route for authenticated users
 * if page was opened from 'office' section
 */

const defaultRoutes = [
  {
    path: '/terms-of-use',
    exact: true,
    component: TermsOfUse,
    key: 'terms-of-use',
    layout: BaseLayout,
    shared: true,
    domain: '*',
    fetchKey: pages.termsOfUse,
    usedInSitemap: true,
    useInSelfServe: true,
    useLang: true,
  },
  {
    path: '/california-notice-at-collection',
    exact: true,
    component: CANoticeAtCollection,
    key: 'california-notice-at-collection',
    layout: BaseLayout,
    shared: true,
    domain: '*',
    fetchKey: pages.californiaNoticeAtCollection,
    usedInSitemap: true,
    useInSelfServe: true,
    useLang: false,
    onlyUS: true,
  },
  {
    path: '/texas-notice-at-collection',
    exact: true,
    component: TexasNoticeAtCollection,
    key: 'texas-notice-at-collection',
    layout: BaseLayout,
    shared: true,
    domain: '*',
    fetchKey: pages.texasNoticeAtCollection,
    usedInSitemap: true,
    useInSelfServe: true,
    useLang: false,
    onlyUS: true,
  },
  {
    path: '/terms-and-conditions',
    exact: true,
    component: TermsAndConditions,
    key: 'terms-and-conditions',
    layout: BaseLayout,
    shared: true,
    domain: '*',
    fetchKey: pages.termsAndConditions,
    useInSelfServe: true,
    usedInSitemap: true,
    useLang: true,
  },
  {
    path: '/privacy-policy',
    exact: true,
    component: PrivacyPolicy,
    key: 'privacy-statement',
    layout: BaseLayout,
    shared: true,
    domain: '*',
    fetchKey: pages.privacyPolicy,
    usedInSitemap: true,
    useInSelfServe: true,
    useLang: true,
  },
  {
    path: '/CASupplyChain',
    exact: true,
    component: CaliforniaTransparencyAct,
    key: 'california-transparency-supply-chains-act',
    layout: BaseLayout,
    shared: true,
    domain: '*',
    fetchKey: pages.californiaTransparencyAct,
    usedInSitemap: true,
    useInSelfServe: true,
    useLang: false,
    onlyUS: true,
  },
  {
    path: '/something-went-wrong',
    component: SomethingWentWrong,
    key: 'something-went-wrong',
    layout: BaseLayout,
    name: 'something-went-wrong',
    domain: '*',
    useInSelfServe: true,
  },
  {
    path: '/unsupported-browser',
    component: UnsupportedBrowser,
    key: 'unsupported-browser',
    layout: BaseLayout,
    name: 'unsupported-browser',
    domain: '*',
    useInSelfServe: true,
  },
];

const selfServeOnlyRoutes = [
  {
    path: '/account/login',
    exact: true,
    component: WaterLogin,
    key: 'account-login',
    layout: BaseLayout,
    domain: REGIONAL_DOMAIN,
    anonymousOnly: true,
  },
  {
    path: '/account/profile',
    exact: true,
    component: PrimoProfile,
    key: 'account-routes',
    layout: BaseLayout,
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
  },
  {
    path: '/account/pay-my-bill',
    exact: true,
    component: PayMyBill,
    key: 'account-routes',
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
    useInMobileAppView: true,
  },
  {
    path: '/acct-xfer',
    exact: true,
    component: AcctXfer,
    key: 'unified-acct-xfer',
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
    useInSelfServe: true,
    usedInSitemap: false,
    useLang: false,
  },
  {
    path: '/refer',
    exact: true,
    component: Refer,
    key: 'refer',
    layout: BaseLayout,
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
    useInSelfServe: true,
  },
  {
    path: '/account/products/list',
    exact: true,
    component: ProductList,
    key: 'account-products',
    layout: BaseLayout,
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
    useInSelfServe: true,
  },
  {
    path: '/account/products/:itemNumber',
    exact: true,
    component: ProductPageLoggedIn,
    key: 'account-product-page',
    layout: BaseLayout,
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
    useInSelfServe: true,
  },
  {
    path: '/account/:activePage',
    component: PrimoAccount,
    key: 'account-routes',
    layout: BaseLayout,
    domain: REGIONAL_DOMAIN,
    privateRoute: true,
  },
];

const regionalSharedRoutes = [
  {
    path: '/support/equipment-manuals',
    component: Support,
    key: 'equipment-manuals',
    layout: BaseLayout,
    name: 'Equipment Manuals',
    shared: true,
    domain: REGIONAL_DOMAIN,
    fetchKey: pages.equipmentManuals,
    usedInSitemap: true,
    useInSelfServe: true,
  },
  {
    path: '/support/contact-us',
    component: Support,
    key: 'contact-us',
    layout: BaseLayout,
    name: 'Contact Us',
    shared: true,
    domain: REGIONAL_DOMAIN,
    fetchKey: pages.contactUs,
    usedInSitemap: true,
    useInSelfServe: true,
  },
  {
    path: '/faqs',
    exact: true,
    component: PrimoFAQ,
    key: 'faq',
    layout: BaseLayout,
    shared: true,
    domain: REGIONAL_DOMAIN,
    fetchKey: pages.faq,
    usedInSitemap: true,
    useInSelfServe: true,
  },
  {
    path: '/page-not-found',
    exact: true,
    component: PageNotFound,
    key: 'page-not-found',
    layout: BaseLayout,
    shared: true,
    domain: REGIONAL_DOMAIN,
    fetchKey: pages.pageNotFound,
    usedInSitemap: false,
  },
  ...selfServeOnlyRoutes,
  ...authRoutes,
  ...defaultRoutes,
];

const regionalRoutes = [...regionalSharedRoutes];

export const sharedRoutes = regionalRoutes
  .filter((route) => route.shared)
  .map((route) => route.path.replace(/:[a-z]+/, ''));

export const getDomainRoutes = () => {
  if (isIE) {
    return [
      {
        path: '*',
        exact: true,
        component: UnsupportedBrowser,
        key: 'unsupported-browser',
        layout: BaseLayout,
        shared: true,
        domain: '*',
        fetchKey: pages.unsupportedBrowser,
        usedInSitemap: false,
      },
    ];
  }

  // All routes applied inside Route Switch, that's why NotFound component should be last in order
  const defaultRoute = {
    path: '*',
    exact: true,
    component: PageNotFound,
    key: 'page-not-found',
    layout: BaseLayout,
    shared: true,
    fetchKey: pages.pageNotFound,
    usedInSitemap: false,
    domain: '*',
  };

  const routes = [...regionalRoutes, defaultRoute];

  return routes;
};

export default regionalRoutes;
